import { Label, Pie, PieChart, Cell, Sector } from 'recharts'
import type { PieSectorShapeProps } from 'recharts/types/polar/Pie'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (nodes 523:7181, 523:7880, 523:9062 — Pie Chart):
 *
 * Variants:
 *   Full / Basic       — Standard pie, no hole
 *   Full / Label       — Pie with outer value labels
 *   Donut / Basic      — Donut with center total text
 *   Donut / Active     — Donut with active sector highlight
 *   Donut / Stacked    — Concentric donut rings
 *   Donut / Interactive — Interactive with center value on hover
 *
 * Specs:
 *   Colors: --chart-1 through --chart-5 via CSS variables
 *   Donut inner radius: ~60% of outer
 *   Center text: text-3xl font-bold + text-xs text-muted-foreground
 *   Legend: colored dots + text-xs labels
 *   Stroke: white separator between sectors
 */

export type PieChartVariant = 'pie' | 'donut' | 'donut-active' | 'donut-stacked'

export interface IntuiPieChartProps {
  /** Chart data — each entry needs a name key + value key + fill color */
  data: Record<string, unknown>[]
  /** Chart configuration (keys, labels, colors) */
  config: ChartConfig
  /** Data key for values */
  dataKey?: string
  /** Data key for category names */
  nameKey?: string
  /** Chart variant */
  variant?: PieChartVariant
  /** Show value labels outside the pie */
  showLabels?: boolean
  /** Show chart legend */
  showLegend?: boolean
  /** Show tooltip on hover */
  showTooltip?: boolean
  /** Center label text (for donut variants) */
  centerLabel?: string
  /** Center value (for donut variants) */
  centerValue?: string | number
  /** Inner radius for donut (0 for full pie) */
  innerRadius?: number
  /** Outer radius */
  outerRadius?: number
  /** Active sector index (for donut-active) */
  activeIndex?: number
  /** Second data ring (for stacked variant) */
  stackedData?: Record<string, unknown>[]
  /** Additional className */
  className?: string
}

function IntuiPieChart({
  data,
  config,
  dataKey = 'visitors',
  nameKey = 'browser',
  variant = 'pie',
  showLabels = false,
  showLegend = false,
  showTooltip = true,
  centerLabel,
  centerValue,
  innerRadius,
  outerRadius,
  activeIndex,
  stackedData,
  className,
}: IntuiPieChartProps) {
  const isDonut = variant !== 'pie'
  const resolvedInner = innerRadius ?? (isDonut ? 60 : 0)
  const resolvedOuter = outerRadius ?? (variant === 'donut-stacked' ? 70 : undefined)

  return (
    <ChartContainer config={config} className={cn('mx-auto aspect-square h-[250px]', className)}>
      <PieChart>
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
        )}
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          innerRadius={resolvedInner}
          outerRadius={resolvedOuter}
          strokeWidth={5}
          stroke="hsl(var(--background))"
          shape={
            variant === 'donut-active'
              ? ({ outerRadius: or = 0, ...props }: PieSectorShapeProps, index: number) => (
                  <Sector {...props} outerRadius={index === activeIndex ? or + 10 : or} />
                )
              : undefined
          }
          label={
            showLabels
              ? ({ payload, ...props }) => (
                  <text
                    cx={props.cx}
                    cy={props.cy}
                    x={props.x}
                    y={props.y}
                    textAnchor={props.textAnchor}
                    dominantBaseline={props.dominantBaseline}
                    fill="hsla(var(--foreground))"
                    className="text-xs"
                  >
                    {payload[dataKey]}
                  </text>
                )
              : undefined
          }
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={(entry.fill as string) || `hsl(var(--chart-${index + 1}))`}
            />
          ))}
          {isDonut && (centerValue !== undefined || centerLabel) && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                      {centerValue !== undefined && (
                        <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                          {typeof centerValue === 'number' ? centerValue.toLocaleString() : centerValue}
                        </tspan>
                      )}
                      {centerLabel && (
                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground text-xs">
                          {centerLabel}
                        </tspan>
                      )}
                    </text>
                  )
                }
              }}
            />
          )}
        </Pie>
        {variant === 'donut-stacked' && stackedData && (
          <Pie
            data={stackedData}
            dataKey={dataKey}
            nameKey={nameKey}
            innerRadius={resolvedOuter ? resolvedOuter + 15 : 85}
            outerRadius={resolvedOuter ? resolvedOuter + 40 : 110}
            strokeWidth={5}
            stroke="hsl(var(--background))"
          >
            {stackedData.map((entry, index) => (
              <Cell key={`stacked-${index}`} fill={(entry.fill as string) || `hsl(var(--chart-${index + 1}))`} />
            ))}
          </Pie>
        )}
        {showLegend && <ChartLegend content={<ChartLegendContent nameKey={nameKey} />} />}
      </PieChart>
    </ChartContainer>
  )
}
IntuiPieChart.displayName = 'IntuiPieChart'

export { IntuiPieChart }
export type { ChartConfig }
